/**
 * The homepage's three examples (SEO_BRIEF_11 section 6): under the link box, "Try an example"
 * with an English talk, a Spanish clip in English and a YouTube video's summary. A tap shows a
 * stored sample at once, with Transcript / Translate / Summary tabs. No job starts and no minutes
 * are used: the samples are a static file (src/examples/home.json, served at
 * /static/data/home-examples.json), written for the site (src/examples/NOTICE.md).
 *
 * ONLY THE BUTTONS ARE IN THE PAGE. The card they open is an inert <template> in
 * app/landing.html, with its words in the page's language, cloned here on the first tap. The
 * samples are fetched on the first sign of a tap (a pointer over or down on the block, or focus
 * inside it), so the homepage's HTML gains three short labels and no sample text, and a visitor
 * who never reaches for a button downloads nothing. The tab roles are added here, with the keys
 * that make them true: left and right arrows, Home and End.
 *
 * ON A PHONE the sample text stops at about 12 lines (site.css), and "Show all" shows only when
 * there is more; once pressed, the whole text shows on every tab of that example. "Now try yours"
 * puts the visitor back in the link box. The shown example's button, pressed again, closes it.
 *
 * COUNTED as `example` with `which` (lib/usage.ts), once per example in a page view.
 */

import { data, el, qs, qsa, setData } from './dom.ts';
import { countUse, EXAMPLE_NAMES, type ExampleName } from './usage.ts';

/**
 * Where the samples are served from: a data file of build/entrypoints.mjs DATA_FILES, outside
 * /static/build/, which the build deletes on every run (lib/pinyin.ts says why).
 */
export const EXAMPLES_PATH = 'data/home-examples.json';

/** The card's tabs, in their order. */
export const EXAMPLE_TABS = ['transcript', 'translation', 'summary'] as const;
export type ExampleTab = (typeof EXAMPLE_TABS)[number];

/** A sample's words, each line with its start in whole seconds, rising. */
export interface ExampleText {
  readonly lang: string;
  readonly lines: readonly (readonly [number, string])[];
}

/** A sample's summary: a sentence, then its points. */
export interface ExampleSummary {
  readonly lang: string;
  readonly lead: string;
  readonly points: readonly string[];
}

export interface Example {
  /** The tab the example opens on: the one its button promises. */
  readonly open: ExampleTab;
  readonly transcript: ExampleText;
  /** The same lines in another language, at the same times. */
  readonly translation: ExampleText;
  readonly summary: ExampleSummary;
}

export type Examples = Readonly<Record<ExampleName, Example>>;

/** A sample is short: the card stops at about 12 lines on a phone. */
export const MAX_LINES = 12;
export const MAX_POINTS = 6;
/** The longest line or point, in characters. */
export const MAX_CHARS = 400;

const LANG = /^[a-z]{2,3}$/;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const isWords = (v: unknown): v is string =>
  typeof v === 'string' && v !== '' && v.trim() === v && v.length <= MAX_CHARS;
const isLang = (v: unknown): v is string => typeof v === 'string' && LANG.test(v);
const isTab = (v: unknown): v is ExampleTab =>
  typeof v === 'string' && (EXAMPLE_TABS as readonly string[]).includes(v);

function parseText(value: unknown): ExampleText | null {
  if (!isRecord(value)) return null;
  const lang = value['lang'];
  const lines = value['lines'];
  if (!isLang(lang) || !Array.isArray(lines)) return null;
  if (lines.length === 0 || lines.length > MAX_LINES) return null;
  const out: (readonly [number, string])[] = [];
  let last = -1;
  for (const line of lines as unknown[]) {
    if (!Array.isArray(line) || line.length !== 2) return null;
    const [at, words] = line as unknown[];
    if (typeof at !== 'number' || !Number.isInteger(at) || at <= last) return null;
    if (!isWords(words)) return null;
    last = at;
    out.push([at, words]);
  }
  return { lang, lines: out };
}

function parseSummary(value: unknown): ExampleSummary | null {
  if (!isRecord(value)) return null;
  const lang = value['lang'];
  const lead = value['lead'];
  const points = value['points'];
  if (!isLang(lang) || !isWords(lead) || !Array.isArray(points)) return null;
  const list = points as unknown[];
  if (list.length === 0 || list.length > MAX_POINTS) return null;
  const out: string[] = [];
  for (const point of list) {
    if (!isWords(point)) return null;
    out.push(point);
  }
  return { lang, lead, points: out };
}

function parseExample(value: unknown): Example | null {
  if (!isRecord(value)) return null;
  const open = value['open'];
  const transcript = parseText(value['transcript']);
  const translation = parseText(value['translation']);
  const summary = parseSummary(value['summary']);
  if (!isTab(open) || transcript === null || translation === null || summary === null) {
    return null;
  }
  // A translation is the transcript's lines in another language, at the same times.
  if (translation.lang === transcript.lang) return null;
  if (translation.lines.length !== transcript.lines.length) return null;
  if (translation.lines.some(([at], i) => at !== transcript.lines[i]?.[0])) return null;
  return { open, transcript, translation, summary };
}

/** The file's three samples, or null when it is anything else. A key off the list is ignored. */
export function parseExamples(value: unknown): Examples | null {
  if (!isRecord(value)) return null;
  const out: Partial<Record<ExampleName, Example>> = {};
  for (const name of EXAMPLE_NAMES) {
    const example = parseExample(value[name]);
    if (example === null) return null;
    out[name] = example;
  }
  return out as Examples;
}

/** The samples' URL, cache-busted with the page's build id like every other asset. */
export function examplesUrl(): string {
  const buildId =
    typeof document === 'undefined'
      ? ''
      : (qs<HTMLMetaElement>('meta[name="build-id"]')?.content.trim() ?? '');
  return `/static/${EXAMPLES_PATH}${buildId ? `?v=${encodeURIComponent(buildId)}` : ''}`;
}

/** One fetch per page, shared by every caller; a failed one is forgotten so a tap can retry. */
let inflight: Promise<Examples> | null = null;

export function loadExamples(fetchImpl: typeof fetch = fetch): Promise<Examples> {
  inflight ??= fetchImpl(examplesUrl(), { credentials: 'omit' })
    .then(async (res) => {
      if (!res.ok) throw new Error(`examples: HTTP ${res.status}`);
      const examples = parseExamples(await res.json());
      if (examples === null) throw new Error('examples: not the three samples');
      return examples;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

/** Forget the fetched samples (tests). */
export function resetExamples(): void {
  inflight = null;
}

/** A line's start as a transcript shows it: 0:05, 1:02, 1:00:05. */
export function formatTime(seconds: number): string {
  const s = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** A language's name in the page's language, or null when this browser cannot name it. */
export function languageName(
  code: string,
  pageLang: string,
  Names: typeof Intl.DisplayNames | undefined = globalThis.Intl?.DisplayNames,
): string | null {
  if (typeof Names !== 'function') return null;
  try {
    const name = new Names([pageLang || 'en', 'en'], { type: 'language' }).of(code);
    return name && name !== code ? name : null;
  } catch {
    return null;
  }
}

/** The line over a translation, "English → Spanish" in the page's language, or null. */
export function translationCaption(
  from: string,
  to: string,
  pageLang: string,
  Names?: typeof Intl.DisplayNames,
): string | null {
  const a = languageName(from, pageLang, Names);
  const b = languageName(to, pageLang, Names);
  if (a === null || b === null) return null;
  return `${a.charAt(0).toLocaleUpperCase(pageLang || 'en')}${a.slice(1)} → ${b}`;
}

/** The tab a key moves to from tab `current` of `count`, or null for any other key. */
export function tabAfter(current: number, key: string, count: number): number | null {
  switch (key) {
    case 'ArrowRight':
      return (current + 1) % count;
    case 'ArrowLeft':
      return (current - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

/** One tab's content: the timed lines (a translation under its two languages), or the summary. */
export function panelNodes(example: Example, tab: ExampleTab, pageLang: string): HTMLElement[] {
  if (tab === 'summary') {
    const { summary } = example;
    return [
      el('div', { lang: summary.lang }, [
        el('p', { class: 'home-example__lead' }, [summary.lead]),
        el(
          'ul',
          { class: 'home-example__points' },
          summary.points.map((point) => el('li', {}, [point])),
        ),
      ]),
    ];
  }
  const text = tab === 'transcript' ? example.transcript : example.translation;
  const nodes: HTMLElement[] = [];
  if (tab === 'translation') {
    const caption = translationCaption(example.transcript.lang, text.lang, pageLang);
    if (caption !== null) nodes.push(el('p', { class: 'home-example__caption' }, [caption]));
  }
  nodes.push(
    el(
      'div',
      { lang: text.lang },
      text.lines.map(([at, words]) =>
        el('p', { class: 'home-example__line' }, [
          el('span', { class: 'home-example__time' }, [formatTime(at)]),
          ' ',
          words,
        ]),
      ),
    ),
  );
  return nodes;
}

/** What `mountExamples` takes from the page, so a test can hand it its own. */
export interface ExamplesEnv {
  fetch?: typeof fetch;
  /** Counts an example opened; `countUse` by default. */
  count?: (which: ExampleName) => void;
  /** The link box "Now try yours" returns to; the page's `[data-url-input]` by default. */
  input?: HTMLElement | null;
  /** The page's language, for the languages' names; `<html lang>` by default. */
  lang?: string;
}

/** The open card's parts, from one clone of the template. */
interface Card {
  readonly tabs: readonly HTMLButtonElement[];
  readonly panel: HTMLElement;
  readonly name: HTMLElement | null;
  readonly more: HTMLButtonElement | null;
  example: Example | null;
  tab: ExampleTab;
  /** "Show all" pressed: the whole text on every tab until another example opens. */
  all: boolean;
}

const nameOf = (button: Element): ExampleName | null => {
  const which = data(button, 'example');
  return which !== null && (EXAMPLE_NAMES as readonly string[]).includes(which)
    ? (which as ExampleName)
    : null;
};

const tabOf = (button: Element): ExampleTab | null => {
  const tab = data(button, 'example-tab');
  return isTab(tab) ? tab : null;
};

/** How far down the screen the card may open before it is scrolled up into view. */
const REVEAL_BELOW = 0.6;

export function mountExamples(root: HTMLElement, env: ExamplesEnv = {}): void {
  const picks = qsa<HTMLButtonElement>('[data-example]', root);
  const region = qs<HTMLElement>('[data-example-region]', root);
  const cardTemplate = qs<HTMLTemplateElement>('template[data-example-template="card"]', root);
  const failedTemplate = qs<HTMLTemplateElement>('template[data-example-template="failed"]', root);
  if (picks.length === 0 || region === null || cardTemplate === null) return;

  const pageLang = env.lang ?? globalThis.document?.documentElement?.lang ?? 'en';
  const load = (): Promise<Examples> =>
    env.fetch === undefined ? loadExamples() : loadExamples(env.fetch);
  const count =
    env.count ?? ((which: ExampleName) => countUse('example', 'home', 'unknown', { which }));
  const counted = new Set<ExampleName>();
  let shown: ExampleName | null = null;
  let request = 0;
  let card: Card | null = null;

  // The samples on the first sign of a tap, so the card can open the moment it lands.
  const warm = (): void => {
    void load().catch(() => undefined);
  };
  for (const type of ['pointerenter', 'pointerdown', 'focusin']) {
    root.addEventListener(type, warm, { once: true, passive: true });
  }

  const labelOf = (which: ExampleName): string =>
    picks.find((pick) => nameOf(pick) === which)?.textContent?.trim() ?? '';

  function press(which: ExampleName | null): void {
    for (const pick of picks) {
      pick.setAttribute(
        'aria-pressed',
        which !== null && nameOf(pick) === which ? 'true' : 'false',
      );
    }
  }

  /** Whether the text runs past its phone cap, and the "Show all" that goes with it. */
  function measure(): void {
    if (card === null || region === null || region.hidden) return;
    const { panel, more } = card;
    setData(panel, 'example-all', card.all ? '' : null);
    const capped = !card.all && panel.scrollHeight > panel.clientHeight + 1;
    setData(panel, 'example-capped', capped ? '' : null);
    if (more) more.hidden = !capped;
  }

  function select(next: ExampleTab, focus = false): void {
    if (card === null || card.example === null) return;
    card.tab = next;
    for (const tab of card.tabs) {
      const on = tabOf(tab) === next;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
      if (on) {
        card.panel.setAttribute('aria-labelledby', tab.id);
        if (focus) tab.focus();
      }
    }
    card.panel.replaceChildren(...panelNodes(card.example, next, pageLang));
    measure();
  }

  function tryYours(): void {
    const input = env.input !== undefined ? env.input : qs<HTMLElement>('[data-url-input]');
    if (!input) return;
    input.focus({ preventScroll: true });
    input.scrollIntoView?.({ block: 'center' });
  }

  /** A fresh clone of the card in the region, wired; null when the template lacks a part. */
  function makeCard(): Card | null {
    if (region === null || cardTemplate === null) return null;
    region.replaceChildren(cardTemplate.content.cloneNode(true));
    const body = qs<HTMLElement>('[data-example-card]', region);
    const tablist = body && qs<HTMLElement>('[data-example-tabs]', body);
    const panel = body && qs<HTMLElement>('[data-example-panel]', body);
    const tabs = body ? qsa<HTMLButtonElement>('[data-example-tab]', body) : [];
    if (!body || !tablist || !panel || tabs.length !== EXAMPLE_TABS.length) return null;

    const head = qs<HTMLElement>('[data-example-head]', body);
    tablist.setAttribute('role', 'tablist');
    if (head?.id) tablist.setAttribute('aria-labelledby', head.id);
    panel.setAttribute('role', 'tabpanel');
    panel.tabIndex = 0;
    const made: Card = {
      tabs,
      panel,
      name: qs<HTMLElement>('[data-example-name]', body),
      more: qs<HTMLButtonElement>('[data-example-more]', body),
      example: null,
      tab: 'transcript',
      all: false,
    };
    for (const tab of tabs) {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      tab.addEventListener('click', () => {
        const next = tabOf(tab);
        if (next !== null) select(next);
      });
    }
    tablist.addEventListener('keydown', (ev: KeyboardEvent) => {
      const next = tabAfter(EXAMPLE_TABS.indexOf(made.tab), ev.key, EXAMPLE_TABS.length);
      const tab = next === null ? undefined : EXAMPLE_TABS[next];
      if (tab === undefined) return;
      ev.preventDefault();
      select(tab, true);
    });
    made.more?.addEventListener('click', () => {
      made.all = true;
      measure();
      made.panel.focus({ preventScroll: true });
    });
    qs<HTMLButtonElement>('[data-example-try]', body)?.addEventListener('click', tryYours);
    return made;
  }

  /** Scrolls the card up into view when it opened low on the screen (a phone, mostly). */
  function reveal(): void {
    if (region === null) return;
    const height = globalThis.innerHeight;
    const top = region.getBoundingClientRect?.().top;
    if (typeof height !== 'number' || height <= 0 || typeof top !== 'number') return;
    if (top > height * REVEAL_BELOW) region.scrollIntoView?.({ block: 'start' });
  }

  function show(which: ExampleName, example: Example): void {
    if (region === null) return;
    // A retry pressed inside the region is replaced by the card: focus goes to its tab.
    const active = globalThis.document?.activeElement ?? null;
    const hadFocus = active !== null && region.contains(active);
    card ??= makeCard();
    if (card === null) return;
    shown = which;
    card.example = example;
    card.all = false;
    if (card.name) card.name.textContent = labelOf(which);
    region.hidden = false;
    select(example.open, hadFocus);
    reveal();
  }

  function failed(which: ExampleName): void {
    if (region === null) return;
    shown = null;
    card = null;
    press(null);
    if (failedTemplate === null) {
      region.replaceChildren();
      region.hidden = true;
      return;
    }
    region.replaceChildren(failedTemplate.content.cloneNode(true));
    region.hidden = false;
    qs<HTMLButtonElement>('[data-example-retry]', region)?.addEventListener('click', () => {
      void open(which);
    });
  }

  async function open(which: ExampleName): Promise<void> {
    const mine = ++request;
    press(which);
    let examples: Examples;
    try {
      examples = await load();
    } catch {
      if (mine === request) failed(which);
      return;
    }
    // A later tap (another example, or this one closed) wins.
    if (mine !== request) return;
    show(which, examples[which]);
    if (!counted.has(which)) {
      counted.add(which);
      count(which);
    }
  }

  function close(): void {
    if (region === null) return;
    request += 1;
    shown = null;
    press(null);
    region.hidden = true;
  }

  for (const pick of picks) {
    pick.addEventListener('click', () => {
      const which = nameOf(pick);
      if (which === null) return;
      if (which === shown && !region.hidden) close();
      else void open(which);
    });
  }

  // The cap is a phone's: a turn of the phone or a wider window can change what fits.
  let frame = false;
  globalThis.addEventListener?.(
    'resize',
    () => {
      if (frame) return;
      frame = true;
      const after =
        globalThis.requestAnimationFrame?.bind(globalThis) ??
        ((fn: () => void) => setTimeout(fn, 16));
      after(() => {
        frame = false;
        measure();
      });
    },
    { passive: true },
  );
  void globalThis.document?.fonts?.ready.then(measure, () => undefined);
}
